/**
 * Note buckets (W2-01a preview-gl; rendering.md R§4.6-R§4.8, R§8 step 5, ARCHITECTURE.md §7.3 step 4).
 *
 * The engine hands over every note quad in Phira's draw order: buckets sorted by (kind order Hold < Drag < Tap <
 * Flick, texture slot rank click < hold < flick < drag < MH set < hold-repeat bodies), quads in push order inside a
 * bucket, vertex colours already clamped to bytes. Each bucket is one draw call with its slot texture (one dynamic
 * vertex buffer, one static index buffer). Corners come near-left, near-right, far-right, far-left; the source
 * rect's top (v0) maps to the far edge (Phira's flip_y). Hold-repeat bodies use their own REPEAT texture (v runs
 * past 1); every other slot is clamped. All note textures are bilinear without mipmaps.
 *
 * Judged play appends Bad-note ghosts as trailing buckets (kind order 4, CR-W1-06-7). Phira draws them after the
 * non-global effects (R§8 item 7), so the renderer draws them in a second phase.
 */

import { NOTE_TEX_SLOTS, type NoteTexSlotName } from '../../core/engine/types';
import { isBadGhostBucket } from '../../core/engine/notes/index';
import type { TexWrap } from '../gl/textures';
import type { LoadedSkin } from '../types';
import { beginQuads, type PassContext } from './passContext';

/** Skin bitmap and wrap mode of a texture slot (null: the skin has none, e.g. no hold-repeat bodies). */
export function slotImage(
  skin: LoadedSkin,
  slot: NoteTexSlotName,
): { readonly image: ImageBitmap; readonly wrap: TexWrap } | null {
  switch (slot) {
    case 'holdBody':
      return skin.holdBody === null ? null : { image: skin.holdBody, wrap: 'repeat' };
    case 'holdBodyMh':
      return skin.holdBodyMh === null ? null : { image: skin.holdBodyMh, wrap: 'repeat' };
    default:
      return { image: skin.images[slot], wrap: 'clamp' };
  }
}

export type NotePhase = 'notes' | 'badGhosts';

/** Draws the note buckets of one phase; returns the number of buckets drawn. */
export function drawNotes(ctx: PassContext, phase: NotePhase): number {
  const skin = ctx.skin;
  const batch = ctx.list.notes;
  if (skin === null || batch.count === 0) return 0;
  const pos = batch.pos;
  const uv = batch.uv;
  const col = batch.color;
  const b = ctx.batch;
  let drawn = 0;
  for (const bucket of batch.buckets) {
    if (isBadGhostBucket(bucket) !== (phase === 'badGhosts')) continue;
    const slotName = NOTE_TEX_SLOTS[bucket.slot];
    if (slotName === undefined || bucket.count <= 0) continue;
    const img = slotImage(skin, slotName);
    if (img === null) continue;
    const tex = ctx.textures.get(img.image, img.wrap, true);
    if (tex === null) continue;
    beginQuads(ctx, ctx.worldToClip, tex);
    const end = Math.min(batch.count, bucket.start + bucket.count);
    for (let q = bucket.start; q < end; q++) {
      const p = q * 8;
      const t = q * 4;
      const u0 = uv[t] ?? 0;
      const v0 = uv[t + 1] ?? 0;
      const u1 = uv[t + 2] ?? 1;
      const v1 = uv[t + 3] ?? 1;
      b.quad(
        pos[p] ?? 0,
        pos[p + 1] ?? 0,
        u0,
        v1,
        pos[p + 2] ?? 0,
        pos[p + 3] ?? 0,
        u1,
        v1,
        pos[p + 4] ?? 0,
        pos[p + 5] ?? 0,
        u1,
        v0,
        pos[p + 6] ?? 0,
        pos[p + 7] ?? 0,
        u0,
        v0,
        col[t] ?? 0,
        col[t + 1] ?? 0,
        col[t + 2] ?? 0,
        col[t + 3] ?? 0,
      );
    }
    // One draw call per bucket, even when two neighbouring buckets share a texture.
    b.flush();
    drawn++;
  }
  return drawn;
}
